import SvgIcon, { SvgIconProps } from "@mui/material/SvgIcon";
import { TreeView } from "@mui/x-tree-view/TreeView";
import { TreeItem, TreeItemContentProps, TreeItemProps, useTreeItem } from "@mui/x-tree-view/TreeItem";
import { CDashboardModuleTabComponent } from "../../DashboardApp/types";
import Typography from "@mui/material/Typography";
import * as React from "react";
import clsx from "clsx";
import { findParentComponent } from "../dom-utils";
import DashboardPageContext from "../DashboardPageContext";

function MinusSquare(props: SvgIconProps) {
	return (
		<SvgIcon fontSize="inherit" style={{ width: 14, height: 14 }} {...props}>
			{/* tslint:disable-next-line: max-line-length */}
			<path d="M22.047 22.074v0 0-20.147 0h-20.12v0 20.147 0h20.12zM22.047 24h-20.12q-.803 0-1.365-.562t-.562-1.365v-20.147q0-.776.562-1.351t1.365-.575h20.147q.776 0 1.351.575t.575 1.351v20.147q0 .803-.575 1.365t-1.378.562v0zM17.873 11.023h-11.826q-.375 0-.669.281t-.294.682v0q0 .401.294 .682t.669.281h11.826q.375 0 .669-.281t.294-.682v0q0-.401-.294-.682t-.669-.281z" />
		</SvgIcon>
	);
}

function PlusSquare(props: SvgIconProps) {
	return (
		<SvgIcon fontSize="inherit" style={{ width: 14, height: 14 }} {...props}>
			{/* tslint:disable-next-line: max-line-length */}
			<path d="M22.047 22.074v0 0-20.147 0h-20.12v0 20.147 0h20.12zM22.047 24h-20.12q-.803 0-1.365-.562t-.562-1.365v-20.147q0-.776.562-1.351t1.365-.575h20.147q.776 0 1.351.575t.575 1.351v20.147q0 .803-.575 1.365t-1.378.562v0zM17.873 12.977h-4.923v4.896q0 .401-.281.682t-.682.281v0q-.375 0-.669-.281t-.294-.682v-4.896h-4.923q-.401 0-.682-.294t-.281-.669v0q0-.401.281-.682t.682-.281h4.923v-4.896q0-.401.294-.682t.669-.281v0q.401 0 .682.281t.281.682v4.896h4.923q.401 0 .682.281t.281.682v0q0 .375-.281.669t-.682.294z" />
		</SvgIcon>
	);
}

const CustomContent = React.forwardRef(function CustomContent(props: TreeItemContentProps, ref: React.Ref<unknown>) {
	const { classes, className, label, nodeId, icon: iconProp, expansionIcon, displayIcon } = props;

	const { disabled, expanded, selected, focused, handleExpansion, handleSelection, preventSelection } = useTreeItem(nodeId);

	const icon = iconProp || expansionIcon || displayIcon;

	const handleMouseDown = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		preventSelection(event);
	};

	const handleExpansionClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		handleExpansion(event);
	};

	const handleSelectionClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		handleSelection(event);
	};

	return (
		<div
			className={clsx(className, classes.root, {
				[classes.expanded]: expanded,
				[classes.selected]: selected,
				[classes.focused]: focused,
				[classes.disabled]: disabled,
			})}
			onMouseDown={handleMouseDown}
			ref={ref as React.Ref<HTMLDivElement>}
		>
			<div onClick={handleExpansionClick} className={classes.iconContainer}>
				{icon}
			</div>
			<Typography onClick={handleSelectionClick} component="div" className={classes.label}>
				{label}
			</Typography>
		</div>
	);
});

const CustomTreeItem = React.forwardRef(function CustomTreeItem(props: TreeItemProps, ref: React.Ref<HTMLLIElement>) {
	return <TreeItem ContentComponent={CustomContent} {...props} ref={ref} />;
});

interface ComponentDomTreeProps {
	component: CDashboardModuleTabComponent;
	selectedComponent: CDashboardModuleTabComponent;
	locateComponent?: CDashboardModuleTabComponent;
	selectionChange: (component: CDashboardModuleTabComponent) => void;
	setLocateComponent: (component: CDashboardModuleTabComponent | undefined) => void;
}

const ComponentDomTree: React.FC<ComponentDomTreeProps> = ({ component, selectedComponent, locateComponent, selectionChange, setLocateComponent }) => {
	const { context, updateContext } = React.useContext(DashboardPageContext);
	const [expandedNodes, setExpandedNodes] = React.useState<string[]>([component.id]);

	const findComponentById = (element: CDashboardModuleTabComponent, cId: string): CDashboardModuleTabComponent | null => {
		if (element.id === cId) return element;
		if (element.childern)
			for (let i = 0; i < element.childern.length; ++i) {
				const com = findComponentById(element.childern[i], cId);
				if (com) return com;
			}
		return null;
	};

	const fireSelectionChanged = (cId: string) => {
		const com = findComponentById(component, cId);
		if (com) selectionChange(com);
	};

	const renderPageDomElementTree = (element: CDashboardModuleTabComponent) => {
		return (
			<CustomTreeItem
				key={element.id}
				nodeId={element.id}
				label={element.type}
				TransitionProps={{
					timeout: 0,
				}}
			>
				{element.childern && element.childern.map((c) => renderPageDomElementTree(c))}
			</CustomTreeItem>
		);
	};

	React.useEffect(() => {
		if (locateComponent) {
			const findComponentPath = (id: string): string[] => {
				let maxDepth = 0;
				const path: string[] = [];
				path.push(id);
				let parent = undefined;
				do {
					parent = findParentComponent(component, id);
					if (parent) {
						path.push(parent.id);
						id = parent.id;
					}
					maxDepth++;
				} while (parent && maxDepth < 100);
				return path.reverse();
			};
			const path = findComponentPath(locateComponent.id);
			const newExpand = [...expandedNodes, ...path];
			setExpandedNodes(newExpand);
			setLocateComponent(undefined);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [component, locateComponent]);

	return (
		<>
			<TreeView
				style={{ width: "max-content" }}
				onNodeSelect={(_event: React.SyntheticEvent, nodeIds: string[] | string) => fireSelectionChanged(nodeIds instanceof Array ? nodeIds[0] : nodeIds)}
				aria-label="customized"
				expanded={expandedNodes}
				onNodeToggle={(event: React.SyntheticEvent, nodeIds: string[]) => {
					setExpandedNodes(nodeIds);
				}}
				selected={selectedComponent.id}
				defaultCollapseIcon={<MinusSquare />}
				defaultExpandIcon={<PlusSquare />}
			>
				{renderPageDomElementTree(component)}
			</TreeView>
		</>
	);
};

export default ComponentDomTree;
